import { Div, Button } from '@codesuma/baseline'
import style from './index.module.css'

// The things that can be done to a row, on a small menu beside the row's own
// dots. The day's tasks and its notes both take it; each says what its own
// options are, and this only puts them on screen.
//
// It hangs off document.body rather than off the row, which is the one thing
// here that drops below Baseline and is worth saying why. The tasks tab scrolls
// its list in a band beside the margin rule, and a scroll container clips
// whatever leans out of it; the slides are moved with transform, and a fixed
// child of a transformed element settles against that element rather than
// against the screen. Neither is somewhere an anchored menu can live, so it goes above both
// and is placed from the dots' own rect. (The platform's own answer to this is
// the popover top layer, which needs Safari 17 — this app still ships to
// iOS 15.)
//
// It closes on a press outside it, on Escape, and on anything that moves the row
// out from under it: a scroll, a resize, or the slide being redrawn. Only one is
// ever open.
//
// Emits 'pick' with the action chosen. The row decides what each one means.

export interface IRowMenuItem {
    action: string
    label: string
    // The one that cannot be taken back reads in the holiday colour.
    danger?: boolean
}

// The one that is open, so a second row's dots put the first one away.
let open: { close: () => void } | null = null

// For a row that is going away with a menu still on it: the menu is hung off
// the body, so nothing else would take it down.
export const closeRowMenu = () => open?.close()

export const RowMenu = (anchor: HTMLElement, items: IRowMenuItem[]) => {
    open?.close()

    const base = Div()
    base.addClass(style.menu)
    base.attr('role', 'menu')

    for (const item of items) {
        const option = Button(item.label)
        option.addClass(style.menuItem)
        if (item.danger) option.addClass(style.menuItemDanger)
        option.attr('role', 'menuitem')
        option.on('click', () => {
            close()
            base.emit('pick', item.action)
        })
        base.append(option)
    }

    document.body.appendChild(base.el)

    // Under the seasons theme each slide is painted its own month's paper, and
    // this menu stands outside every slide — the root still holds whichever month
    // the calendar was last showing, which is not always the day being read. So
    // the colour comes from the row the menu belongs to. A custom property, so
    // setProperty rather than the style helper.
    base.el.style.setProperty('--paper', getComputedStyle(anchor).getPropertyValue('--paper'))

    // Under the dots when the screen has room for it, over them when it does
    // not, and never past either side.
    const gap = 6
    const edge = 8
    const a = anchor.getBoundingClientRect()
    const box = base.el.getBoundingClientRect()
    const below = a.bottom + gap
    base.style({
        top: `${below + box.height <= window.innerHeight - edge ? below : Math.max(edge, a.top - gap - box.height)}px`,
        left: `${Math.min(Math.max(edge, a.left + a.width / 2 - box.width / 2), window.innerWidth - box.width - edge)}px`,
    })

    // The press that opened this is still travelling, and the dots are outside
    // the menu, so a plain "outside closes it" would shut it again on the way
    // up. The anchor is spared instead.
    const onPress = (e: Event) => {
        const target = e.target as Node
        if (!base.el.contains(target) && !anchor.contains(target)) close()
    }
    const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') close()
    }
    // Capture, because the list scrolls in its own band rather than on the page.
    document.addEventListener('pointerdown', onPress, true)
    document.addEventListener('keydown', onKey)
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)

    function close() {
        if (open !== handle) return
        open = null
        document.removeEventListener('pointerdown', onPress, true)
        document.removeEventListener('keydown', onKey)
        window.removeEventListener('scroll', close, true)
        window.removeEventListener('resize', close)
        base.el.remove()
    }

    const handle = Object.assign(base, { close })
    open = handle
    return handle
}

// The dots that open it. Shared so a task's row and a note's are the same tap in
// the same place, with only the label saying which row it belongs to.
const DOTS_SVG = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/></svg>'

export const RowMenuButton = (label: string) => {
    const base = Button()
    base.addClass(style.rowMore)
    base.attr('aria-label', label)
    base.attr('aria-haspopup', 'menu')
    base.html(DOTS_SVG)
    return base
}
